import { ApplicationForm, StepId, isPrepay, totalOwnershipPercent } from './types';
import { effectiveLocationAddress } from './locations';

export type Errors = Record<string, string>;

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;
const YEAR_RE = /^(18|19|20)\d{2}$/;
const EIN_OR_SSN_RE = /^(\d{2}-?\d{7}|\d{3}-?\d{2}-?\d{4})$/;
const SSN_RE = /^\d{3}-?\d{2}-?\d{4}$/;

const req = (v: string) => (v && v.trim() ? '' : 'Required');

// Per-step validation. Returns a map of fieldKey -> message (empty map = valid).
// Repeatable rows use dotted keys, e.g. "locations.0.line1".
export function validateStep(stepId: StepId, form: ApplicationForm): Errors {
  const e: Errors = {};
  const set = (k: string, msg: string) => {
    if (msg) e[k] = msg;
  };

  switch (stepId) {
    case 'business-name':
      set('legalName', req(form.legalName));
      break;

    case 'business-type':
      set('businessType', form.businessType ? '' : 'Pick one to continue.');
      break;

    case 'business-entity':
      set('entityType', form.entityType ? '' : 'Pick one to continue.');
      if (!form.yearIncorporated.trim()) set('yearIncorporated', 'Required');
      else if (!YEAR_RE.test(form.yearIncorporated.trim()))
        set('yearIncorporated', 'Enter a 4-digit year.');
      set('stateOfIncorporation', req(form.stateOfIncorporation));
      if (!form.ein.trim()) set('ein', 'Required');
      else if (!EIN_OR_SSN_RE.test(form.ein.trim()))
        set('ein', 'Enter a 9-digit EIN (12-3456789) or SSN.');
      break;

    case 'business-office':
      set('officeLine1', req(form.officeLine1));
      set('officeCity', req(form.officeCity));
      set('officeState', req(form.officeState));
      if (!form.officeZip.trim()) set('officeZip', 'Required');
      else if (!ZIP_RE.test(form.officeZip.trim())) set('officeZip', 'Enter a 5-digit ZIP.');
      set('officePhone', req(form.officePhone));
      break;

    case 'owner':
      set('ownerFirstName', req(form.ownerFirstName));
      set('ownerLastName', req(form.ownerLastName));
      set('ownerMobile', req(form.ownerMobile));
      if (!form.ownerEmail.trim()) set('ownerEmail', 'Required');
      else if (!EMAIL_RE.test(form.ownerEmail.trim())) set('ownerEmail', 'Enter a valid email.');
      if (!form.password) set('password', 'Required');
      else if (form.password.length < 8) set('password', 'Use at least 8 characters.');
      if (!form.passwordConfirm) set('passwordConfirm', 'Required');
      else if (form.password !== form.passwordConfirm)
        set('passwordConfirm', 'Passwords must match.');
      set('heardAboutUs', form.heardAboutUs ? '' : 'Pick one to continue.');
      break;

    case 'verify':
      // Validated by the server code check; the step gates on emailVerified in the page.
      break;

    case 'billing':
      set('billingContact', req(form.billingContact));
      set('billingPhone', req(form.billingPhone));
      if (!form.billingEmail.trim()) set('billingEmail', 'Required');
      else if (!EMAIL_RE.test(form.billingEmail.trim()))
        set('billingEmail', 'Enter a valid email.');
      break;

    case 'locations':
      if (!form.locations.length) set('locations', 'Add at least one location.');
      form.locations.forEach((l, i) => {
        // A "same as previous" row mirrors the office/prior location, so validate
        // the resolved address — the stored fields are intentionally left empty.
        const addr = l.sameAsPrevious ? effectiveLocationAddress(form, i) : l;
        set(`locations.${i}.line1`, req(addr.line1));
        set(`locations.${i}.city`, req(addr.city));
        set(`locations.${i}.state`, req(addr.state));
        if (!addr.zip.trim()) set(`locations.${i}.zip`, 'Required');
        else if (!ZIP_RE.test(addr.zip.trim())) set(`locations.${i}.zip`, 'Enter a 5-digit ZIP.');
      });
      break;

    case 'credit':
      set('creditLine', form.creditLine ? '' : 'Pick one to continue.');
      set('annualTonsRange', form.annualTonsRange ? '' : 'Pick one to continue.');
      break;

    case 'principals': {
      const prepay = isPrepay(form);
      if (!form.principals.length) set('principals', 'Add at least one principal.');
      form.principals.forEach((p, i) => {
        set(`principals.${i}.fullName`, req(p.fullName));
        set(`principals.${i}.homeLine1`, req(p.homeLine1));
        set(`principals.${i}.homeCity`, req(p.homeCity));
        set(`principals.${i}.homeState`, req(p.homeState));
        set(`principals.${i}.homeZip`, req(p.homeZip));
        // Each owner must state a valid ownership percentage (0–100).
        const pct = p.percentOwned.trim();
        if (!pct) set(`principals.${i}.pct`, 'Required');
        else {
          const n = Number(pct);
          if (!Number.isFinite(n) || n < 0 || n > 100) set(`principals.${i}.pct`, 'Enter 0–100.');
        }
        if (!prepay) {
          if (!p.ssn.trim()) {
            // On a resubmit an SSN already on file doesn't need re-entering.
            if (!p.ssnOnFile) set(`principals.${i}.ssn`, 'Required');
          } else if (!SSN_RE.test(p.ssn.trim())) {
            set(`principals.${i}.ssn`, 'Enter a 9-digit SSN.');
          }
        }
      });
      // All owners together must account for 100% of the business.
      const ownership = totalOwnershipPercent(form.principals);
      if (form.principals.length && Math.abs(ownership - 100) > 0.01) {
        set(
          'principalsTotal',
          `Ownership must total 100% — it currently adds up to ${ownership}%.`
        );
      }
      break;
    }

    case 'bank':
      if (isPrepay(form)) break;
      set('bankName', req(form.bankName));
      set('bankPhone', req(form.bankPhone));
      set('bankLine1', req(form.bankLine1));
      break;

    case 'references': {
      if (isPrepay(form)) break;
      // "See attached" waives the three structured references.
      if (form.tradeReferenceFileName) break;
      const norm = (s: string) => s.trim().toLowerCase();
      const digits = (s: string) => s.replace(/\D/g, '');
      // All three trade references are required and must be complete.
      form.references.forEach((r, i) => {
        set(`references.${i}.companyName`, req(r.companyName));
        set(`references.${i}.phone`, req(r.phone));
        if (!r.email.trim()) set(`references.${i}.email`, 'Required');
        else if (!EMAIL_RE.test(r.email.trim()))
          set(`references.${i}.email`, 'Enter a valid email.');

        // A reference duplicates an earlier one if it shares the company name,
        // phone, or email — so the same supplier can't be listed twice.
        const isDuplicate = form.references.some((o, j) => {
          if (j >= i) return false;
          const sameCompany = !!r.companyName.trim() && norm(o.companyName) === norm(r.companyName);
          const samePhone = !!digits(r.phone) && digits(o.phone) === digits(r.phone);
          const sameEmail = !!r.email.trim() && norm(o.email) === norm(r.email);
          return sameCompany || samePhone || sameEmail;
        });
        if (isDuplicate && !e[`references.${i}.companyName`]) {
          set(`references.${i}.companyName`, 'This reference duplicates an earlier one.');
        }
      });
      break;
    }

    case 'bankruptcy':
      if (isPrepay(form)) break;
      if (!form.hasBankruptcy) set('hasBankruptcy', 'Pick one to continue.');
      else if (form.hasBankruptcy === 'yes' && !form.bankruptcyExplanation.trim())
        set('bankruptcyExplanation', 'Tell us what happened.');
      break;

    case 'team':
      form.teamMembers.forEach((m, i) => {
        set(`teamMembers.${i}.name`, req(m.name));
        if (!m.email.trim()) set(`teamMembers.${i}.email`, 'Required');
        else if (!EMAIL_RE.test(m.email.trim()))
          set(`teamMembers.${i}.email`, 'Enter a valid email.');
      });
      break;

    case 'products':
      if (!form.productInterestIds.length) set('products', 'Pick at least one product.');
      break;

    case 'tax':
      if (!form.taxExempt) set('taxExempt', 'Pick one to continue.');
      else if (form.taxExempt === 'yes') {
        // We generate the resale certificate from these details, so they're all
        // required (no file upload).
        set('resaleCertState', req(form.resaleCertState));
        set('resaleSellerType', req(form.resaleSellerType));
        set('resaleRegistrationNumber', req(form.resaleRegistrationNumber));
        set('resaleDescription', req(form.resaleDescription));
      }
      break;

    case 'terms': {
      const prepay = isPrepay(form);
      if (!form.agreedToTerms) set('agreedToTerms', 'You need to agree to continue.');
      if (!prepay && !form.agreedPersonalGuarantee)
        set('agreedPersonalGuarantee', 'The credit terms require a personal guarantee.');
      if (form.signerIndex === '') set('signerIndex', 'Choose the principal who is signing.');
      set('signerTitle', req(form.signerTitle));
      if (!form.signerTypedSignature.trim())
        set('signerTypedSignature', 'Type your full name to sign.');
      else {
        const signer = form.principals[Number(form.signerIndex)];
        if (
          signer &&
          form.signerTypedSignature.trim().toLowerCase() !== signer.fullName.trim().toLowerCase()
        )
          set('signerTypedSignature', `Type the signer's name exactly: ${signer.fullName}`);
      }
      break;
    }

    case 'review':
      if (!form.notRobot) set('notRobot', 'Complete the captcha to submit.');
      break;
  }

  return e;
}

export const isStepValid = (stepId: StepId, form: ApplicationForm): boolean =>
  Object.keys(validateStep(stepId, form)).length === 0;
